import { Component, OnInit, ViewChildren, QueryList, ElementRef, AfterViewInit, ViewChild } from '@angular/core';
import { Observable, take, combineLatest, tap, map, BehaviorSubject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import * as fromState from '@app/store/state/ai/ai.state';
import { AppService } from '@app/services/app/app.service';
import { AIService } from '@app/services/app/ai.service';

import { TitleService } from '@core/services/title.service';
import { User } from '@core/objects/user';

@Component({
	selector: 'ai-main',
	templateUrl: './main.component.html',
	styleUrls: ['./main.component.scss'],
})
export class AIMainComponent implements OnInit, AfterViewInit {
	@ViewChildren('scrollableAIChatEntry') scrollableEntries!: QueryList<ElementRef>;

	@ViewChild('taContainer') textareaContainer!: ElementRef;
	@ViewChild('textArea') textareaElement!: ElementRef;
	
	public readonly ENTRY_TYPES = fromState.EntryType;
	public readonly KPI_ACCESS = "access_kpi_assistant_feature";

	public readonly LOADING = 0;
	public readonly DONE = 1;
	public readonly NOT_FOUND = 2;

	public pageStatus: number = this.LOADING;
	public initialized: boolean = false;
	public questionReady: boolean = false;
	public question: string = '';
	public displayIntro: boolean = false;
	public user: User = null;
    public AIPluginAccess: boolean;

	private scrollIndex: number = -1;
	private questionIndex: number = -1;  // Track the index of the current question
	private inHistory: boolean = false; // Flag to control updates


	entries$: Observable<fromState.AIEntry[]>;
	loading$: Observable<boolean>;
	initialized$: Observable<boolean>;
	errorWarmUp$: Observable<HttpErrorResponse>;
	errorQuestion$: Observable<HttpErrorResponse>;
	errorAnswer$: Observable<HttpErrorResponse>;
	session$: Observable<any>;

	constructor(
        private titleSrv: TitleService,
        private app: AppService,
		private aiSrv: AIService,
	) { }

	ngOnInit(): void {
		this.entries$ = this.aiSrv.getAllEntries();
		this.loading$ = this.aiSrv.getLoadingStatus();
		this.initialized$ = this.aiSrv.getInitializedStatus();
		this.errorWarmUp$ = this.aiSrv.getWarmUpError();
		this.errorQuestion$ = this.aiSrv.getQuestionError();
		this.errorAnswer$ = this.aiSrv.getAnswerError();
		this.session$ = this.app.getSession();

		this.session$.subscribe( (session: any) => {
            if (session == null || !session.current.user )
                return;
			this.user = session.current.user;

			this.AIPluginAccess = session.options?.access.hasOwnProperty(this.KPI_ACCESS)? session.options.access[this.KPI_ACCESS] : false;

			if(!this.AIPluginAccess)
				this.pageStatus = this.NOT_FOUND;
			else
				this.initView();
        });

		// Combine both observables to handle initialization status and warm-up errors
		combineLatest([this.initialized$, this.errorWarmUp$]).subscribe(([initialized, warmUpError]) => {
			if (initialized) {
				this.initialized = true;
				// If initialized is true, proceed with initializing the view
				this.initView();
			} else if (warmUpError || !this.AIPluginAccess) {
				// Show some UI message or handle the error case
				this.pageStatus = this.NOT_FOUND;
			} else {
				// If not initialized and no error, perform the warm-up
				this.aiSrv.warmUp();
			}
		});

	}

	ngAfterViewInit() : void {
		// Subscribe to entries$ to detect changes
		this.entries$.pipe(
			tap(() => {
			  // Wait for the view to update with new entries
			  setTimeout(() => this.scrollToLastEntry(), 0);
			})
		  ).subscribe();

	}

	toggleIntro() : void {
		this.displayIntro = !this.displayIntro;
	}

	autoAsk(event: any, question: string) : void {
		event.preventDefault()
		this.ask(question);
	}

	onQuestionPromptChange(text: string): void {
		this.question = text;
		this.autoSizePrompt();
		if (text === '') {
			this.questionIndex = -1;
		}
		this.questionReady = (text.trim() !== '');
	}

	onQuestionSubmit(event) : void {
		event.preventDefault();
		if(this.question.trim() === '')
			return;
		// send question to backend
		this.displayIntro = false; // maybe remove
		this.ask(this.question);
	}

	// https://www.w3.org/TR/uievents-key/#key-ArrowUp
	onPromptKeyPress(keyEvent: KeyboardEvent) : void {
		this.autoSizePrompt();
		if (keyEvent.key === 'ArrowUp') {
			this.getPreviousQuestion();
		  } else if (keyEvent.key === 'ArrowDown') {
			this.getNextQuestion();
		  }
	}

	private initView() : void {
        this.titleSrv.set("AI");
		if(!this.user || !this.AIPluginAccess)
			return;
		this.pageStatus = this.DONE;
		this.autoSizePrompt();
	}

	private ask(question: string): void {
		this.loading$.pipe(take(1)).subscribe((isLoading: boolean) => {
			if (!isLoading) {
				// Proceed to ask the question since loading is false
				this.aiSrv.sendQuestion(question);
				this.question = '';
				this.questionReady = false;
				this.questionIndex = -1;
			} 
			// else {
			// 	console.log('Loading in progress, please wait...');
			// }
		});
	}

	private scrollToLastEntry(): void {
		const items = this.scrollableEntries.toArray();
		if (items.length > 3 && this.scrollIndex != items.length) {
			window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
		}
	}

	/**  Manage history  **/
	// Retrieve the previous question from entries$
	private  getPreviousQuestion() {
		this.inHistory = true;
		this.entries$
			.pipe(
				map(entries => entries.filter(entry => entry.type === 'question')),
				take(1)  // Take only the first result
			)
			.subscribe(questions => {
				if (this.questionIndex === -1 || this.questionIndex > 0) {
					this.questionIndex = this.questionIndex === -1 ? questions.length - 1 : this.questionIndex - 1;
				  }
				  this.updateQuestionFromEntries();  // Update the question based on the new index
			});
	}

	// Retrieve the next question from entries$
	private getNextQuestion() {
		this.inHistory = true;
		this.entries$
			.pipe(
				map(entries => entries.filter(entry => entry.type === 'question')),
				take(1)  // Take only the first result
			)
			.subscribe(questions => {
				if (this.questionIndex < questions.length - 1) {
					this.questionIndex++;
				} else {
					// this.questionIndex = -1;  // Set index to out of bounds
					this.question = '';  // Clear question when index is out of bounds
					this.questionReady = false;
					return;
				}
				this.updateQuestionFromEntries();  // Update the question based on the new index
			});
	}

	// Update question only when necessary
	private updateQuestionFromEntries() {
		if (!this.inHistory) {
			return; // Prevent updates while the flag is true
		}
		this.entries$
			.pipe(
				map(entries => entries.filter(entry => entry.type === 'question')),
				take(1)  // Take the latest snapshot of questions
			)
			.subscribe(questions => {
				if (this.questionIndex >= 0 && this.questionIndex < questions.length) {
					this.question = questions[this.questionIndex].value.question;  // Update the question property
					this.questionReady = true;
				} else {
					this.question = '';  // Clear the question if the index is out of range
					this.questionReady = false;
				}
				this.inHistory = false;
			});
	}

	private autoSizePrompt() {
		
		setTimeout(() => {
			const taDivContainer = this.textareaContainer.nativeElement;
			const ta = this.textareaElement.nativeElement;
			ta.style.cssText = 'height:0px';
			let height = Math.min(50 * 5, ta.scrollHeight);
			taDivContainer.style.cssText = 'height:' + height + 'px';
			ta. style.cssText = 'height:' + height + 'px';
		},0);
	}
}

